import React, { useEffect, useState } from 'react';
import Loader from '@amzn/meridian/loader';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Button from '@amzn/meridian/button';
import Text from '@amzn/meridian/text';
import useGetBatchAssortmentSwapRecommendations from '@store/hooks/useGetBatchAssortmentSwapRecommendations';
import {
  AgileBatchRecommedationInput,
  AgileRecommendationInput,
  AgileRecommendationResult,
  AgileRecommendationTypes,
} from '@components/agile/types/AgileRecommendationTypes';
import AtRecommendationTable from '@components/agile/agileComponents/AtAcrossZoneOperation/AtAcrossZoneComponents/AtAcrossZoneRecommendationTableComponent/AtRecommendationTable';
import { AgileMacroplanogram } from '@components/agile/types/AgileMacroplanogramTypes';
import { AssortmentFacingUpdate, ErrorType, SwapAssortmentInput } from 'src/graphql/generated/graphql';
import useBatchSwapAssortmentAcrossZone from 'src/store/hooks/useBatchSwapAssortmentAcrossZone';
import AgileUploadRow from 'src/components/agile/types/AgileUploadRow';
import { mergeDuplicateProductChanges } from 'src/components/agile/utils/RecommendationTableUtils';
import SwapAssortmentConfirmationModalComponent from './AtAcrossZoneRecommendationTableComponent/SwapAssortmentConfirmationModalComponent';

export interface AtBulkUploadDiffTableProps {
  listOfMacroplanogram: AgileMacroplanogram[] | undefined;
  recommendationInput: AgileBatchRecommedationInput[];
  swapTime: number;
  diffTableData: AgileUploadRow[];
  macroplanogramToFacingUnitsMap: Record<string, number>;
  macroplanogramToSubcategoryFacingUnitsMap: Record<string, Record<string, number>>;
  setInputTableFlag: (value: boolean) => void;
  setRecommendationFlag: (value: boolean) => void;
  updatedRecommendInputWithDiffData: AgileBatchRecommedationInput[];
}

const AtAcrossZoneRecommedationTable = (props: AtBulkUploadDiffTableProps) => {
  const {
    recommendationInput,
    swapTime,
    listOfMacroplanogram,
    diffTableData,
    macroplanogramToFacingUnitsMap,
    macroplanogramToSubcategoryFacingUnitsMap,
    setInputTableFlag,
    setRecommendationFlag,
    updatedRecommendInputWithDiffData,
  } = props;
  const [disableButtonMap, setDisableButtonMap] = useState<Map<string, boolean>>(new Map());
  const [openConfirmationModal, setOpenConfirmationModal] = useState<boolean>(false);
  const [openErrorModal, setOpenErrorModal] = useState(false);
  const [openSuccessModal, setOpenSuccessModal] = useState(false);
  const [batchSwapInput, setBatchSwapInput] = useState<SwapAssortmentInput[]>([]);
  const [filteredBatchSwapInput, setFilteredBatchSwapInput] = useState<SwapAssortmentInput[]>([]);

  const [macroplanogramDataMap, setMacroplanogramDataMap] = useState<
    Record<
      string,
      {
        filterFacingUpdatesList: AgileRecommendationTypes[];
        uniqueNewAsinList: AgileRecommendationInput[];
      }
    >
  >({});
  const [rowDataMap, setRowDataMap] = useState<Map<string, AgileRecommendationResult>>(new Map());
  const [errorMap, setErrorMap] = useState<
    {
      key: string;
      error: { errorMessage: string; errorType?: ErrorType | null };
    }[]
  >([]);
  const { mutateAsync: batchSwapAssortmentAcrossZone } = useBatchSwapAssortmentAcrossZone();
  const { isLoading, recommendations } = useGetBatchAssortmentSwapRecommendations({
    batchAssortmentSwap: recommendationInput,
  });
  const onBatchSwapAssortmentAcrossZone = async () => {
    await batchSwapAssortmentAcrossZone({
      input: {
        swapAssortmentInputList: filteredBatchSwapInput,
      },
    }).then((resp) => {
      // eslint-disable-next-line promise/always-return
      if (resp.batchSwapAssortmentAcrossZone.errorMap.length === 0) {
        setOpenSuccessModal(true);
      } else {
        console.error('error occured during batch swap', resp.batchSwapAssortmentAcrossZone.errorMap);
        setErrorMap(resp.batchSwapAssortmentAcrossZone.errorMap);
        setOpenErrorModal(true);
      }
    });
  };

  useEffect(() => {
    if (recommendations && recommendations.length > 0) {
      const newDisableButtonMap = new Map<string, boolean>();
      const newRowDataMap = new Map<string, AgileRecommendationResult>();

      recommendations.forEach((recommendation) => {
        const zoneDetails = listOfMacroplanogram?.find(
          (macroplanogramDetails) => macroplanogramDetails.entryId === recommendation.macroplanogramId,
        );

        const hasZoneData = zoneDetails?.storeSubcategories?.some(
          (sc) => sc.zoneFixtureIds && sc.zoneFixtureIds.length > 0,
        );

        if (hasZoneData && zoneDetails?.storeSubcategories) {
          // Aggregate model: one entry per subcategory
          zoneDetails.storeSubcategories.forEach((subcategory) => {
            const key = `${recommendation.macroplanogramId || ''}-${subcategory.subcategoryId}`;
            newDisableButtonMap.set(key, true);

            const subcategoryResults = recommendation.results.filter(
              (asin) => asin.subcategoryId === subcategory.subcategoryId,
            );

            const subcategoryOccupiedFacings = subcategoryResults.reduce((sum, asin) => sum + asin.availableFacings, 0);

            const subcategoryMaxFacingUnits = Number(subcategory.maxFacingUnits) || recommendation.maxFacingUnits;

            newRowDataMap.set(key, {
              ...recommendation,
              results: subcategoryResults,
              maxFacingUnits: subcategoryMaxFacingUnits,
              occupiedFacingUnits: subcategoryOccupiedFacings,
            });
          });
        } else {
          // Per-zone model: one entry per macroplanogram
          const key = recommendation.macroplanogramId || '';
          newDisableButtonMap.set(key, true);
          newRowDataMap.set(key, recommendation);
        }
      });

      setDisableButtonMap(newDisableButtonMap);
      setRowDataMap(newRowDataMap);
    }
  }, [recommendations, listOfMacroplanogram]);

  const handleDisableButton = (key: string, newDisableButtonState: boolean) => {
    setDisableButtonMap((prev) => {
      const updated = new Map(prev);
      updated.set(key, newDisableButtonState);
      return updated;
    });
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const notEmpty = (...arrays: (any[] | undefined)[]) => arrays.some((array) => array && array.length > 0);

  const recommendationDataHandler = (key: string, recommendation: AgileRecommendationTypes[]) => {
    setRowDataMap((prev) => {
      const updated = new Map(prev);
      const existing = updated.get(key);
      if (existing) {
        updated.set(key, {
          ...existing,
          results: recommendation,
        });
      }
      return updated;
    });
  };
  const handleSubmitSelection = () => {
    const nonZeroFacingAssortmentsByZone = updatedRecommendInputWithDiffData.map((inputAssortment) => {
      const assortmentsWithNonZeroFacings = inputAssortment.addAssortment.filter(
        (assortment) => +assortment.facingUnits !== 0,
      );
      return {
        ...inputAssortment,
        addAssortment: assortmentsWithNonZeroFacings,
      };
    });
    const filteredSwapInput: SwapAssortmentInput[] = [];

    // Convert rowDataMap to array for processing
    const rowDataArray = Array.from(rowDataMap.values());

    const AssortmentListToUpdateByZone = rowDataArray.map((data) => ({
      macroplanogramId: data.macroplanogramId,
      assortmentFacingUpdates: data.results.map((assortment) => ({
        entryId: assortment.entryId,
        facingUnits: parseInt(String(assortment.remainingFacings), 10),
        fohCapacity: assortment.remainingCapacity,
      })),
    }));

    const SwapInput = nonZeroFacingAssortmentsByZone.map((newAssortment) => {
      const assortmentToUpdateByZone = AssortmentListToUpdateByZone.find(
        (assortmentToUpdate) => assortmentToUpdate.macroplanogramId === newAssortment.macroplanogramId,
      );
      if (assortmentToUpdateByZone) {
        return {
          ...newAssortment,
          assortmentFacingUpdates: assortmentToUpdateByZone.assortmentFacingUpdates,
        };
      }
      return { ...newAssortment, assortmentFacingUpdates: [] };
    });
    const macroDataMap: Record<
      string,
      {
        filterFacingUpdatesList: AgileRecommendationTypes[];
        uniqueNewAsinList: AgileRecommendationInput[];
      }
    > = {};
    listOfMacroplanogram?.forEach((macroplanogram) => {
      // Aggregate results for this macroplanogram across all subcategories
      const macroResults = rowDataArray
        .filter((data) => data.macroplanogramId === macroplanogram.entryId)
        .flatMap((data) => data.results);

      const { uniqueRecommendedAsinList, uniqueNewAsinList } = mergeDuplicateProductChanges(
        nonZeroFacingAssortmentsByZone.find((input) => input.macroplanogramId === macroplanogram.entryId)
          ?.addAssortment || [],
        macroResults,
      );

      const filterFacingUpdatesList = uniqueRecommendedAsinList?.filter(
        (data) =>
          +data.difference !== +0 ||
          Math.trunc((data.fohCapacity / data.availableFacings) * data.remainingFacings - data.remainingCapacity) !== 0,
      );
      if (notEmpty(filterFacingUpdatesList, uniqueNewAsinList)) {
        filteredSwapInput.push({
          storeId: macroplanogram.storeId,
          swapTime,
          endTime: macroplanogram.endTime,
          macroplanogramId: macroplanogram.entryId,
          addAssortment: uniqueNewAsinList,
          assortmentFacingUpdates: filterFacingUpdatesList?.map(
            (assortment) =>
              ({
                entryId: assortment.entryId,
                facingUnits: parseInt(String(assortment.remainingFacings), 10),
                fohCapacity: assortment.remainingCapacity,
              }) as AssortmentFacingUpdate,
          ),
          isDraft: false,
        } as SwapAssortmentInput);
        macroDataMap[macroplanogram.entryId] = {
          filterFacingUpdatesList,
          uniqueNewAsinList,
        };
      }
    });
    setMacroplanogramDataMap(macroDataMap);
    setBatchSwapInput(SwapInput);
    setFilteredBatchSwapInput(filteredSwapInput);
    setOpenConfirmationModal(true);
  };

  // Check if all buttons are enabled (none are disabled)
  const allButtonsEnabled = Array.from(disableButtonMap.values()).every((disabled) => !disabled);

  return isLoading && recommendations === undefined ? (
    <Column alignmentHorizontal="center" alignmentVertical="center" spacing="800" spacingInset="500">
      <Loader type="circular" size="large" />{' '}
    </Column>
  ) : (
    <>
      <Row spacing="400" alignmentHorizontal="justify" spacingInset="400">
        <Text type="h400">Adjust existing capacity of products in the zones.</Text>
      </Row>
      {recommendations &&
        recommendations
          .flatMap((recommendation) => {
            const zoneDetails = listOfMacroplanogram?.find(
              (macroplanogramDetails) => macroplanogramDetails.entryId === recommendation.macroplanogramId,
            );

            // Check if aggregate model (has zone-specific data)
            const hasZoneData = zoneDetails?.storeSubcategories?.some(
              (sc) => sc.zoneFixtureIds && sc.zoneFixtureIds.length > 0,
            );

            if (hasZoneData && zoneDetails?.storeSubcategories) {
              // New aggregate model: Show one table per subcategory
              return zoneDetails.storeSubcategories.map((subcategory) => {
                const tableKey = `${recommendation.macroplanogramId || ''}-${subcategory.subcategoryId}`;

                // Get data from map
                const subcategoryRecommendation = rowDataMap.get(tableKey);
                if (!subcategoryRecommendation) return null;

                // Get new facings being added for THIS SUBCATEGORY ONLY
                const macroId = recommendation.macroplanogramId || '';
                const subcategoryNewFacings =
                  macroplanogramToSubcategoryFacingUnitsMap[macroId]?.[subcategory.subcategoryId] || 0;

                // Create subcategory-specific facing units map
                const subcategoryFacingUnitsMap = {
                  [zoneDetails.entryId]: subcategoryNewFacings,
                };

                return (
                  <AtRecommendationTable
                    key={tableKey}
                    tableKey={tableKey}
                    recommendation={subcategoryRecommendation}
                    disableButtonState={handleDisableButton}
                    recommendationDataHandler={recommendationDataHandler}
                    macroplanogramToFacingUnitsMap={subcategoryFacingUnitsMap}
                    zoneDetails={zoneDetails}
                    subcategoryName={subcategory.subcategoryName}
                  />
                );
              });
            } else {
              // Old per-zone model: One table per macroplanogram
              const tableKey = recommendation.macroplanogramId || '';
              const zoneRecommendation = rowDataMap.get(tableKey);
              if (!zoneRecommendation) return null;

              return (
                <AtRecommendationTable
                  key={tableKey}
                  tableKey={tableKey}
                  recommendation={zoneRecommendation}
                  disableButtonState={handleDisableButton}
                  recommendationDataHandler={recommendationDataHandler}
                  macroplanogramToFacingUnitsMap={macroplanogramToFacingUnitsMap}
                  zoneDetails={zoneDetails}
                />
              );
            }
          })
          .filter(Boolean)}
      <Row width="100%" alignmentHorizontal="center" spacingInset="300" backgroundColor="subdued">
        <Button onClick={handleSubmitSelection} disabled={!allButtonsEnabled}>
          Review changes
        </Button>
      </Row>
      <SwapAssortmentConfirmationModalComponent
        openConfirmationModal={openConfirmationModal}
        setOpenConfirmationModal={setOpenConfirmationModal}
        openErrorModal={openErrorModal}
        setOpenErrorModal={setOpenErrorModal}
        openSuccessModal={openSuccessModal}
        setOpenSuccessModal={setOpenSuccessModal}
        batchSwapInput={batchSwapInput}
        listOfMacroplanogram={listOfMacroplanogram}
        diffTableData={diffTableData}
        onBatchSwapAssortmentAcrossZone={onBatchSwapAssortmentAcrossZone}
        errorMap={errorMap}
        macroplanogramDataMap={macroplanogramDataMap}
        setInputTableFlag={setInputTableFlag}
        setRecommendationFlag={setRecommendationFlag}
      />
    </>
  );
};

export default AtAcrossZoneRecommedationTable;
